import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { Capacitor } from '@capacitor/core'
import { f7 } from "framework7-vue";

/**
 * Canonical responsive breakpoints — aligned with Framework7 v9's own rules:
 *
 *  - Actions convert to Popovers when a target element is present AND
 *    (app.width >= 768 || device.desktop || iOS iPad)   [actions-class.js]
 *  - Popups switch to the centered "tablet" layout at
 *    @media (min-width: 630px) and (min-height: 630px); the
 *    `tablet-fullscreen` option keeps them edge-to-edge   [framework7.css]
 *  - F7 inset/typography steps: 480 (xsmall) / 568 (small) / 768 (medium) /
 *    1024 (large); tab bar switches to tablet sizing at 768
 *
 * Therefore the app canon is:
 *   phone/small : width < 768
 *   tablet      : width >= 768 (tablet UI affordances; overlaps large)
 *   large/desk  : width >= 1024 (desktop toolbar, split panes, wide toast)
 *   tablet-full : width >= 630 AND height >= 630 (F7 popup "tablet" rule —
 *                 what `tablet-fullscreen` opts out of)
 */
export const BREAKPOINTS = {
  /** Phone upper bound (exclusive): F7 master-detail + actions-popover threshold */
  PHONE_MAX: 767,
  /** Tablet lower bound: F7 medium step, tab-bar tablet sizing, master-detail */
  TABLET_MIN: 768,
  /** Large screen / desktop: F7 large step, desktop toolbar, wide toast */
  LARGE_MIN: 1024,
  /** F7 popup "tablet" layout threshold (width AND height) */
  TABLET_FULLSCREEN_MIN: 630,
}

/**
 * Reactive CSS media query hook (best practice for responsive breakpoints).
 * Mirrors the query's matches state and stays in sync on viewport/zoom/
 * orientation changes via the native matchMedia listener.
 *
 * Initializes synchronously (window.matchMedia is available in browser), so
 * consumers can read the value during setup without a post-mount flash.
 */
export function useMediaQuery(query) {
  const matches = ref(false)
  let mql = null

  function update() {
    if (mql) matches.value = mql.matches
  }

  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    mql = window.matchMedia(query)
    matches.value = mql.matches
  }

  onMounted(() => {
    if (!mql) return
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', update)
    } else {
      window.addEventListener('resize', update)
      window.addEventListener('orientationchange', update)
    }
  })

  onBeforeUnmount(() => {
    if (!mql) return
    if (typeof mql.removeEventListener === 'function') {
      mql.removeEventListener('change', update)
    } else {
      window.removeEventListener('resize', update)
      window.removeEventListener('orientationchange', update)
    }
    mql = null
  })

  return { matches }
}

/** Small screens (phones): max-width 767px. */
export function useSmallScreen() {
  return useMediaQuery(`(max-width: ${BREAKPOINTS.PHONE_MAX}px)`)
}

/** Large screens: width >= 1024px (F7 large step; desktop toolbar, wide toast). */
export function useLargeScreen() {
  return useMediaQuery(`(min-width: ${BREAKPOINTS.LARGE_MIN}px)`)
}

/**
 * Tablet-sized screens: width >= 768px (F7 medium step — master-detail split,
 * tab-bar tablet sizing, actions render as popovers). Overlaps large screens,
 * mirroring the previous ≥568 semantics.
 */
export function useTabletScreen() {
  return useMediaQuery(`(min-width: ${BREAKPOINTS.TABLET_MIN}px)`)
}

/**
 * F7 popup "tablet" layout: width >= 630px AND height >= 630px.
 * This is the dimension Framework7 itself uses to center popups; the
 * `tablet-fullscreen` option keeps a popup edge-to-edge there. Use this hook
 * when a component must agree with F7's popup/tablet-fullscreen decision.
 */
export function useTabletFullscreen() {
  return useMediaQuery(
    `(min-width: ${BREAKPOINTS.TABLET_FULLSCREEN_MIN}px) and (min-height: ${BREAKPOINTS.TABLET_FULLSCREEN_MIN}px)`
  )
}

/**
 * Desktop layout: large screen AND non-native platform.
 * Replaces `isDeskTop` (which is only ever true on the web).
 */
export function useDesktopScreen() {
  const { matches: largeScreen } = useLargeScreen()
  return computed(() => largeScreen.value && !Capacitor.isNativePlatform())
}

/**
 * Touch capability detection.
 * Returns true if the device supports touch events.
 */
export function useTouchSupport() {
  const hasTouch = ref(false)

  onMounted(() => {
    hasTouch.value = f7.support.touch || 'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      (window.DocumentTouch && document instanceof window.DocumentTouch)
  })

  return { hasTouch }
}

/**
 * Tablet with touch capability detection.
 * Returns true on tablet-sized screens with touch capability.
 * Used for determining when to use full-screen layout on tablets.
 */
export function useTabletWithTouch() {
  const { matches: isTablet } = useTabletScreen()
  const { hasTouch } = useTouchSupport()

  return computed(() => isTablet.value && hasTouch.value)
}

/**
 * Full-screen layout detection.
 * Returns true when the app should use the single-column layout:
 * - width < 1024 (F7 large step): phones, portrait tablets (touch — AppLayout
 *   adds .layout--tablet-full for a full-bleed main) and narrow desktop
 *   windows (main capped at 568px centered by app.css).
 * Returns false from 1024px up, where the `.layout_content` grid shows the
 * fixed 568px main plus the left/right rails — the same width at which
 * landscape tablets (iPad landscape = 1024) fit all three columns.
 */
export function useFullScreenLayout() {
  const { matches: isLargeScreen } = useLargeScreen()

  return computed(() => !isLargeScreen.value)
}

/**
 * Combined hook for layout decisions.
 * Returns all layout-related media query states.
 */
export function useLayoutQueries() {
  const smallScreen = useSmallScreen()
  const largeScreen = useLargeScreen()
  const tabletScreen = useTabletScreen()
  const tabletFullscreen = useTabletFullscreen()
  const desktopScreen = useDesktopScreen()
  const touchSupport = useTouchSupport()
  const tabletWithTouch = useTabletWithTouch()
  const fullScreenLayout = useFullScreenLayout()

  return {
    smallScreen,
    largeScreen,
    tabletScreen,
    tabletFullscreen,
    desktopScreen,
    touchSupport,
    tabletWithTouch,
    fullScreenLayout
  }
}
